import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarCheck, Flame, Loader2, Sparkles, Trophy } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { doCheckin } from "@/lib/boide-api";
import { loadCheckinStatus, loadMyStreak } from "@/lib/bootstrap";
import { useBoideUser } from "@/hooks/use-boide-user";
import { CheckinCycle } from "@/components/checkin-cycle";

const vnd = (n: number | null | undefined) =>
  typeof n === "number" ? n.toLocaleString("vi-VN") : "--";

const REMINDER_KEY = "checkin_reminder_date";

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Pill điểm danh ở header: số chuỗi + chấm đỏ nhắc, popover lịch 7 ngày + điểm danh nhanh. */
export function CheckinButton() {
  const { user, refresh } = useBoideUser();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const status = useQuery({
    queryKey: ["checkin-status"],
    queryFn: loadCheckinStatus,
    enabled: !!user,
    staleTime: 60_000,
  });
  const data = status.data;
  const boiStreak = useQuery({
    queryKey: ["streak-me"],
    queryFn: loadMyStreak,
    enabled: !!user,
    staleTime: 60_000,
  });

  const checkin = useMutation({
    mutationFn: doCheckin,
    onSuccess: (res) => {
      if (res.success) {
        toast.success(res.message, {
          description: `+${vnd(res.reward)}đ · chuỗi ${res.streak} ngày`,
        });
        refresh();
      } else {
        toast.info(res.message);
      }
      qc.invalidateQueries({ queryKey: ["checkin-status"] });
      qc.invalidateQueries({ queryKey: ["streak-me"] });
      setOpen(false);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Điểm danh thất bại"),
  });

  // Đóng popover khi click ra ngoài
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Nhắc nhở 1 lần/ngày khi chưa điểm danh
  useEffect(() => {
    if (!data || data.checked_in_today) return;
    if (localStorage.getItem(REMINDER_KEY) === today()) return;
    localStorage.setItem(REMINDER_KEY, today());

    const action = { label: "Điểm danh", onClick: () => checkin.mutate() };
    if (data.current_streak > 0) {
      toast.warning(`Điểm danh để giữ chuỗi ${data.current_streak} ngày!`, {
        description: `Bỏ lỡ hôm nay là chuỗi reset. Nhận ${vnd(data.next_reward)}đ ngay.`,
        action,
        duration: 8000,
      });
    } else {
      toast.info("Hôm nay bạn chưa điểm danh", {
        description: `Điểm danh nhận ${vnd(data.next_reward)}đ.`,
        action,
        duration: 6000,
      });
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) return null;

  const streak = data?.current_streak ?? 0;
  const notYet = !!data && !data.checked_in_today;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label="Điểm danh"
        onClick={() => setOpen((v) => !v)}
        className="relative flex items-center gap-1.5 rounded-full border border-gold-soft/50 bg-card/50 px-2.5 py-1.5 text-xs font-semibold text-primary transition hover:border-primary/60 hover:shadow-gold sm:text-sm"
      >
        <Flame fill="currentColor" className={`h-3.5 w-3.5 ${notYet ? "text-orange-400" : "text-primary"}`} />
        <span>{streak}</span>
        {notYet && (
          <span className="absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
          </span>
        )}
      </button>

      {open && (
        <div className="fixed left-3 right-3 top-[60px] w-auto sm:absolute sm:inset-auto sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-72 z-50 overflow-hidden rounded-2xl border border-gold-soft/40 bg-popover shadow-mystic backdrop-blur-xl animate-mystic-rise">
          <div className="flex items-center gap-2 border-b border-border/40 px-4 py-3">
            <CalendarCheck className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-foreground">Điểm danh hằng ngày</span>
          </div>

          <div className="px-3 py-3">
            {!data ? (
              <div className="h-20 animate-pulse rounded-lg bg-muted/20" />
            ) : (
              <>
                <CheckinCycle cycle={data.cycle} compact />
                <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Flame fill="currentColor" className="h-3.5 w-3.5 text-orange-400" />
                  Chuỗi điểm danh:{" "}
                  <span className="font-semibold text-foreground">{data.current_streak} ngày</span>
                </div>
                <div className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  Chuỗi bói:{" "}
                  <span className="font-semibold text-foreground">{boiStreak.data?.current_streak ?? 0} ngày</span>
                </div>
                <button
                  type="button"
                  onClick={() => checkin.mutate()}
                  disabled={data.checked_in_today || checkin.isPending}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-gold-gradient px-3 py-2 text-sm font-semibold text-primary-foreground shadow-gold transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {checkin.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {data.checked_in_today
                    ? "Đã điểm danh hôm nay"
                    : `Điểm danh nhận ${vnd(data.next_reward)}đ`}
                </button>
              </>
            )}
          </div>

          <Link
            to="/diem-danh"
            onClick={() => setOpen(false)}
            className="flex items-center justify-center gap-2 border-t border-border/40 px-4 py-2.5 text-sm text-foreground/80 transition hover:bg-accent/50 hover:text-foreground"
          >
            <Trophy className="h-4 w-4 text-primary" />
            Xem bảng xếp hạng chuỗi bói
          </Link>
        </div>
      )}
    </div>
  );
}
